import React, { useEffect, useRef, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import { createRoot, Root } from "react-dom/client";
import { CoamAreaFeature } from "../api/coamArea";
import { RagMarker, RagMarkerData } from "../components/RagMarker";

interface UseRagMarkersLayerOptions {
  map: mapboxgl.Map | null;
  features: CoamAreaFeature[];
  onSelect?: (feature: CoamAreaFeature) => void;
}

type MarkerEntry = {
  id: string;
  marker: mapboxgl.Marker;
  root: Root;
  data: RagMarkerData;
};

export const useRagMarkersLayer = ({
  map,
  features,
  onSelect,
}: UseRagMarkersLayerOptions) => {
  const markersRef = useRef<Map<string, MarkerEntry>>(new Map());
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach(({ marker, root }) => {
      marker.remove();
      queueMicrotask(() => {
        try {
          if (root) {
            root.unmount();
          }
        } catch (e) {
          console.error("Error unmounting marker:", e);
        }
      });
    });
    markersRef.current.clear();
  }, []);

  const upsertMarkers = useCallback(
    (mapInstance: mapboxgl.Map, geoFeatures: CoamAreaFeature[]) => {
      const seen = new Set<string>();
      const zoom = mapInstance.getZoom();

      geoFeatures.forEach((feature) => {
        const { longitudeCentroid, latitudeCentroid, name, ragStatus, id } =
          feature.properties;

        if (!ragStatus) return;

        const lng = parseFloat(longitudeCentroid);
        const lat = parseFloat(latitudeCentroid);

        if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;

        const fid = String(id ?? `${lng},${lat},${name}`);
        seen.add(fid);

        const markerData: RagMarkerData = {
          time: ragStatus.time,
          cost: ragStatus.cost,
          quality: ragStatus.quality,
          areaName: name,
        };

        const existing = markersRef.current.get(fid);
        if (existing) {
          existing.root.render(
            React.createElement(RagMarker, {
              data: markerData,
              zoomLevel: zoom,
            })
          );
          existing.marker.setLngLat([lng, lat]);
          existing.data = markerData;
          return;
        }

        const markerElement = document.createElement("div");
        markerElement.style.cursor = "pointer";
        markerElement.addEventListener("click", (event) => {
          event.stopPropagation();
          onSelectRef.current?.(feature);
        });
        const root = createRoot(markerElement);
        root.render(
          React.createElement(RagMarker, {
            data: markerData,
            zoomLevel: zoom,
          })
        );

        const marker = new mapboxgl.Marker({
          element: markerElement,
          anchor: "bottom",
          offset: [0, -(mapInstance.getZoom() + 2 || 0)],
        })
          .setLngLat([lng, lat])
          .addTo(mapInstance);

        markersRef.current.set(fid, {
          id: fid,
          marker,
          root,
          data: markerData,
        });
      });

      markersRef.current.forEach((entry, key) => {
        if (!seen.has(key)) {
          entry.marker.remove();
          markersRef.current.delete(key);
          queueMicrotask(() => {
            try {
              entry.root.unmount();
            } catch (e) {
              console.error("Error unmounting marker:", e);
            }
          });
        }
      });
    },
    []
  );

  useEffect(() => {
    if (!map || features.length === 0) return;

    const handleInitialize = () => {
      upsertMarkers(map, features);
    };

    const handleZoom = () => {
      const zoom = map.getZoom();
      markersRef.current.forEach(({ root, data }) => {
        root.render(
          React.createElement(RagMarker, {
            data,
            zoomLevel: zoom,
          })
        );
      });
    };
    if (map.loaded()) {
      handleInitialize();
    }

    map.once("idle", handleInitialize);
    map.on("style.load", handleInitialize);
    map.on("zoom", handleZoom);

    return () => {
      map.off("style.load", handleInitialize);
      map.off("zoom", handleZoom);
      map.off("idle", handleInitialize);
      clearMarkers();
    };
  }, [map, features, upsertMarkers, clearMarkers]);
};
